import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SCORECARD_PILLARS } from '@/config/scorecardPillars';
import type { AvailableMetricRecommendations, SelectedRecommendation } from '@/types/rebuild';

interface RecommendationPickerProps {
  available: AvailableMetricRecommendations[];
  loading?: boolean;
  onStart: (selected: SelectedRecommendation[]) => void;
}

export function RecommendationPicker({
  available,
  loading,
  onStart,
}: RecommendationPickerProps) {
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const byMetric = useMemo(() => {
    const map = new Map<string, AvailableMetricRecommendations>();
    for (const a of available) map.set(a.metric_key, a);
    return map;
  }, [available]);

  const allBulletIds = useMemo(
    () => available.flatMap((m) => m.bullets.map((b) => b.recommendation_id)),
    [available],
  );

  const allSelected =
    allBulletIds.length > 0 && allBulletIds.every((id) => checked.has(id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setChecked(new Set());
    } else {
      setChecked(new Set(allBulletIds));
    }
  };

  const toggle = (id: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleExpand = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleStart = () => {
    const selected: SelectedRecommendation[] = [];
    for (const item of available) {
      for (const bullet of item.bullets) {
        if (checked.has(bullet.recommendation_id)) {
          selected.push({
            recommendation_id: bullet.recommendation_id,
            metric_key: item.metric_key,
            recommendation_text: bullet.text,
            what_does_not: item.what_does_not,
          });
        }
      }
    }
    onStart(selected);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-white/55 font-sans max-w-2xl">
          Select the scorecard recommendations you want applied to your script. Each bullet becomes
          a targeted AI edit you can review and revert.
        </p>
        {allBulletIds.length > 0 && (
          <button
            type="button"
            onClick={toggleSelectAll}
            className="shrink-0 text-xs font-sans font-semibold uppercase tracking-wider text-gold-button hover:text-gold-light transition-colors"
          >
            {allSelected ? 'Deselect all' : 'Select all'}
          </button>
        )}
      </div>

      {SCORECARD_PILLARS.map((pillar) => {
        const metrics = pillar.subMetrics
          .map((sm) => byMetric.get(sm.summaryKey))
          .filter(Boolean) as AvailableMetricRecommendations[];
        if (!metrics.length) return null;

        return (
          <div key={pillar.id} className="border border-[#2a2a2a] rounded-lg overflow-hidden">
            <div className="bg-[#141414] px-4 py-3 border-b border-[#2a2a2a]">
              <h3 className="text-sm font-sans font-semibold text-gold-button uppercase tracking-wider">
                {pillar.title}
              </h3>
            </div>
            <div className="divide-y divide-[#2a2a2a]">
              {metrics.map((metric) => {
                const isOpen = expanded.has(metric.metric_key);
                return (
                  <div key={metric.metric_key} className="bg-[#0d0d0d]">
                    <button
                      type="button"
                      className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-white/5"
                      onClick={() => toggleExpand(metric.metric_key)}
                    >
                      {isOpen ? (
                        <ChevronDown className="h-4 w-4 text-white/40 shrink-0" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-white/40 shrink-0" />
                      )}
                      <span className="text-sm font-sans font-medium text-white">
                        {metric.metric_key}
                      </span>
                      <span className="text-xs text-white/40 font-sans ml-auto">
                        {metric.score_label}
                      </span>
                    </button>
                    {isOpen && (
                      <div className="px-4 pb-4 space-y-2">
                        {metric.what_does_not && (
                          <p className="text-xs text-white/45 font-sans italic pl-6">
                            {metric.what_does_not.split('\n')[0]}
                          </p>
                        )}
                        {metric.bullets.map((b) => (
                          <label
                            key={b.recommendation_id}
                            className="flex items-start gap-3 pl-6 cursor-pointer group"
                          >
                            <input
                              type="checkbox"
                              checked={checked.has(b.recommendation_id)}
                              onChange={() => toggle(b.recommendation_id)}
                              className="mt-1 accent-amber-500"
                            />
                            <span className="text-sm text-white/70 font-sans group-hover:text-white/90">
                              {b.text}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      <div className="flex justify-end pt-2">
        <Button
          disabled={loading || checked.size === 0}
          onClick={handleStart}
          className="bg-gold-button text-black hover:bg-gold-light font-sans font-semibold uppercase tracking-wider rounded-sm"
        >
          Start rebuild ({checked.size} selected)
        </Button>
      </div>
    </div>
  );
}
